import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { Logo } from "@/components/layout/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError, login } from "@/lib/api";
import { useSession } from "@/lib/session";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { setUser } = useSession();

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const user = await login(email.trim().toLowerCase(), password);
      setUser(user);
      // Onda 12.3: cotistas vão direto pro portal segregado
      const target = user.role === "cotista" ? "/investidor/posicoes" : "/app";
      navigate(target, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === "invalid_credentials") {
        setError("E-mail ou senha incorretos.");
      } else {
        setError("Não foi possível entrar agora. Tente novamente em instantes.");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex bg-muted/40">
      <div className="hidden lg:flex w-1/2 bg-primary-dark text-white relative overflow-hidden">
        <div className="absolute inset-0 gradient-hero opacity-30" />
        <div className="relative z-10 p-12 flex flex-col justify-between w-full">
          <Logo onDark />
          <div className="space-y-4 max-w-md">
            <ShieldCheck className="h-10 w-10 text-accent" />
            <h2 className="font-display font-bold text-3xl text-balance">
              Plataforma de risco de FIDCs de consignado CLT.
            </h2>
            <p className="text-white/80 text-balance">
              Conciliação diária originador ↔ bancarizador ↔ custodiante e fingerprint contínuo
              de CNPJs e CPFs em uma única tela.
            </p>
          </div>
          <div className="text-xs text-white/60">
            © {new Date().getFullYear()} CredCapital · Conexão criptografada (TLS 1.3)
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col">
        <header className="p-6">
          <Button asChild variant="ghost" size="sm">
            <Link to="/">
              <ArrowLeft className="h-4 w-4" /> Voltar
            </Link>
          </Button>
        </header>

        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md space-y-8">
            <div className="space-y-2 lg:hidden">
              <Logo />
            </div>
            <div className="space-y-2">
              <h1 className="text-2xl font-display font-bold text-primary-dark">Acesse a plataforma</h1>
              <p className="text-sm text-muted-foreground">
                Use o e-mail e senha cadastrados pelo administrador CredCapital.
              </p>
            </div>

            <form className="space-y-5" onSubmit={submit}>
              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="voce@credcapital.net"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••"
                />
              </div>
              {error && (
                <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                  {error}
                </div>
              )}
              <Button type="submit" className="w-full" size="lg" disabled={loading}>
                {loading ? "Entrando..." : "Entrar"}
              </Button>
            </form>

            <div className="text-xs text-center">
              <Link to="/forgot-password" className="text-primary-medium hover:underline">Esqueceu a senha?</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
